import { useCallback, useEffect, useRef, useState } from 'react'

const STORAGE_PREFIX = 'didge-review-session:'

type ReviewSessionTimeMode = 'active' | 'display'

interface SessionStorageValue {
  accumulatedMs: number
}

const storageKey = (projectId: string, taskId: string) =>
  `${STORAGE_PREFIX}${projectId}:${taskId}`

const readStoredMs = (projectId: string, taskId: string): number => {
  if (!projectId || !taskId) {
    return 0
  }
  try {
    const raw = sessionStorage.getItem(storageKey(projectId, taskId))
    if (!raw) {
      return 0
    }
    const parsed = JSON.parse(raw) as SessionStorageValue
    return Math.max(0, Number(parsed.accumulatedMs) || 0)
  } catch {
    return 0
  }
}

const writeStoredMs = (projectId: string, taskId: string, ms: number) => {
  if (!projectId || !taskId) {
    return
  }
  sessionStorage.setItem(
    storageKey(projectId, taskId),
    JSON.stringify({ accumulatedMs: Math.max(0, Math.floor(ms)) }),
  )
}

interface UseReviewSessionTimeOptions {
  disabled?: boolean
  initialSeconds?: number
  onFlush?: (accumulatedSeconds: number) => void
}

export const useReviewSessionTime = (
  projectId: string,
  taskId: string | undefined,
  mode: ReviewSessionTimeMode,
  options: UseReviewSessionTimeOptions = {},
) => {
  const disabled = options.disabled ?? false
  const initialSeconds = Math.max(0, Number(options.initialSeconds) || 0)
  const onFlush = options.onFlush
  const [sessionSeconds, setSessionSeconds] = useState(initialSeconds)

  const accumulatedMsRef = useRef(0)
  const segmentStartRef = useRef<number | null>(null)
  const isRunningRef = useRef(false)
  const onFlushRef = useRef(onFlush)
  onFlushRef.current = onFlush

  const getCurrentMs = useCallback(() => {
    let total = accumulatedMsRef.current
    if (isRunningRef.current && segmentStartRef.current != null) {
      total += performance.now() - segmentStartRef.current
    }
    return total
  }, [])

  const getAccumulatedSeconds = useCallback(
    () => Math.floor(getCurrentMs() / 1000),
    [getCurrentMs],
  )

  const syncDisplay = useCallback(() => {
    setSessionSeconds(Math.floor(getCurrentMs() / 1000))
  }, [getCurrentMs])

  const persist = useCallback(
    (flushRemote = false) => {
      if (!projectId || !taskId) {
        return getAccumulatedSeconds()
      }
      const total = getCurrentMs()
      accumulatedMsRef.current = total
      segmentStartRef.current = null
      isRunningRef.current = false
      writeStoredMs(projectId, taskId, total)
      syncDisplay()
      const seconds = Math.floor(total / 1000)
      if (flushRemote) {
        onFlushRef.current?.(seconds)
      }
      return seconds
    },
    [getAccumulatedSeconds, getCurrentMs, projectId, syncDisplay, taskId],
  )

  const resume = useCallback(() => {
    if (disabled || mode !== 'active') {
      return
    }
    segmentStartRef.current = performance.now()
    isRunningRef.current = true
  }, [disabled, mode])

  const pauseSessionTime = useCallback(() => {
    if (isRunningRef.current) {
      persist(true)
    }
  }, [persist])

  const flushSessionTime = useCallback(() => {
    const seconds = persist(false)
    if (!disabled && mode === 'active') {
      resume()
    }
    return seconds
  }, [disabled, mode, persist, resume])

  useEffect(() => {
    if (!projectId || !taskId) {
      return
    }
    const seeded = Math.max(
      readStoredMs(projectId, taskId),
      initialSeconds * 1000,
      getCurrentMs(),
    )
    accumulatedMsRef.current = seeded
    if (isRunningRef.current) {
      segmentStartRef.current = performance.now()
    }
    writeStoredMs(projectId, taskId, seeded)
    syncDisplay()
  }, [getCurrentMs, initialSeconds, projectId, syncDisplay, taskId])

  useEffect(() => {
    if (mode !== 'active' || disabled || !projectId || !taskId) {
      return
    }

    resume()
    const onBeforeUnload = () => pauseSessionTime()
    window.addEventListener('beforeunload', onBeforeUnload)
    const tick = window.setInterval(syncDisplay, 1000)

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      window.clearInterval(tick)
      pauseSessionTime()
    }
  }, [disabled, mode, pauseSessionTime, projectId, resume, syncDisplay, taskId])

  useEffect(() => {
    if (mode !== 'display' || !projectId || !taskId) {
      return
    }

    const refresh = () => {
      accumulatedMsRef.current = Math.max(
        readStoredMs(projectId, taskId),
        initialSeconds * 1000,
      )
      syncDisplay()
    }

    refresh()
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [initialSeconds, mode, projectId, syncDisplay, taskId])

  return {
    sessionSeconds,
    getAccumulatedSeconds,
    flushSessionTime,
    pauseSessionTime,
  }
}
